<template>
    <Form :validation-schema="schema" v-slot="{ handleSubmit, errors }" as="div" ref="quoteForm">
        <div class="row">
            <div class="col-md-7">
                <div class="card">
                    <div class="card-header py-2">General Information</div>
                    <div class="card-body">
                        <div class="row g-3">
                            <div class="col-md-6">
                                <label for="name" class="form-label">Select Customer <span class="text-danger">*</span></label>
                                <Field
                                    name="name"
                                    :value="customerValue"
                                    v-slot="{ field }"
                                >
                                    <vue-multiselect
                                        v-bind="field"
                                        v-model="quotation.customer"
                                        :class="{ 'is-invalid': errors.name }"
                                        :options="customerOptions"
                                        placeholder="Search or select customer"
                                        tag-placeholder="Add this as new customer"
                                        :taggable="false"
                                        :show-labels="false"
                                        track-by="name_with_address"
                                        label="name_with_address"
                                        @tag="addCustomer"
                                        @select="selectCustomer"
                                    ></vue-multiselect>
                                </Field>
                                <div v-if="errors.name" class="is-invalid invalid-feedback">{{ errors.name }}</div>
                            </div>
                            <div class="col-md-6">
                                <label for="property" class="form-label">Select Property <span class="text-danger">*</span></label>
                                <Field
                                    name="property"
                                    :value="propertyValue"
                                    v-slot="{ field }"
                                >
                                    <vue-multiselect
                                        v-bind="field"
                                        v-model="quotation.property"
                                        :class="{ 'is-invalid': errors.property }"
                                        :options="propertyOptions"
                                        placeholder="Search or select property"
                                        tag-placeholder="Add this as new property"
                                        :taggable="false"
                                        :show-labels="false"
                                        track-by="full_address"
                                        label="full_address"
                                        @select="selectProperty"
                                    ></vue-multiselect>
                                </Field>
                                <div v-if="errors.property" class="is-invalid invalid-feedback">{{ errors.property }}</div>
                            </div>
                            <div class="col-md-6">
                                <label for="email" class="form-label">Email <span class="text-danger">*</span></label>
                                <Field
                                    type="email"
                                    class="form-control"
                                    name="email"
                                    placeholder="Enter email"
                                    v-model="quotation.email"
                                    :class="{ 'is-invalid': errors.email }"
                                ></Field>
                                <div v-if="errors.email" class="is-invalid invalid-feedback">{{ errors.email }}</div>
                            </div>
                            <div class="col-md-6">
                                <label for="phone" class="form-label">Phone <span class="text-danger">*</span></label>
                                <Field
                                    type="text"
                                    class="form-control"
                                    name="phone"
                                    placeholder="Enter phone"
                                    v-model="quotation.phone"
                                    :class="{ 'is-invalid': errors.phone }"
                                ></Field>
                                <div v-if="errors.phone" class="is-invalid invalid-feedback">{{ errors.phone }}</div>
                            </div>
                            <div class="col-md-6">
                                <label for="address" class="form-label">Address <span class="text-danger">*</span></label>
                                <Field
                                    type="text"
                                    class="form-control"
                                    name="address"
                                    placeholder="Enter address"
                                    v-model="quotation.address"
                                    :class="{ 'is-invalid': errors.address }"
                                ></Field>
                                <div v-if="errors.address" class="is-invalid invalid-feedback">{{ errors.address }}</div>
                            </div>
                            <div class="col-md-6">
                                <label for="postalCode" class="form-label">Postal Code <span class="text-danger">*</span></label>
                                <Field
                                    type="text"
                                    class="form-control"
                                    name="postal_code"
                                    placeholder="Enter postalCode"
                                    v-model="quotation.postal_code"
                                    :class="{ 'is-invalid': errors.postal_code }"
                                ></Field>
                                <div v-if="errors.postal_code" class="is-invalid invalid-feedback">{{ errors.postal_code }}</div>
                            </div>
                            <div class="col-6">
                                <label for="discount" class="form-label">Status</label>
                                <vue-multiselect
                                    v-model="quotation.status"
                                    :options="[{name: 'pending', label: 'Pending'}, {name: 'confirmed', label: 'Confirmed'}, {name: 'cancelled', label: 'Cancelled'}]"
                                    :searchable="false"
                                    :show-labels="true"
                                    track-by="label"
                                    label="label"
                                ></vue-multiselect>
                            </div>
                            <div class="col-md-6">
                                <label for="template" class="form-label">Select Template <span class="text-danger">*</span></label>
                                <Field
                                    name="template"
                                    :value="templateValue"
                                    v-slot="{ field }"
                                >
                                    <vue-multiselect
                                        v-bind="field"
                                        v-model="quotation.template"
                                        :class="{ 'is-invalid': errors.template }"
                                        :options="templateOptions"
                                        placeholder="Search or add new customer"
                                        tag-placeholder="Add this as new customer"
                                        :taggable="true"
                                        :show-labels="false"
                                        track-by="name"
                                        label="name"
                                        @select="selectTemplate"
                                    ></vue-multiselect>
                                </Field>
                                <div v-if="errors.template" class="is-invalid invalid-feedback">{{ errors.template }}</div>
                            </div>
                            <div class="col-md-6">
                                <label for="price" class="form-label">Price <span class="text-danger">*</span></label>
                                <Field
                                    type="text"
                                    class="form-control"
                                    name="price"
                                    placeholder="Enter price"
                                    v-model="quotation.price"
                                    :class="{ 'is-invalid': errors.price }"
                                ></Field>
                                <div v-if="errors.price" class="is-invalid invalid-feedback">{{ errors.price }}</div>
                            </div>
                            <div class="col-md-6">
                                <label for="price" class="form-label">Valid Until <span class="text-danger">*</span></label>
                                <VueDatePicker
                                    name="end_date"
                                    v-model="quotation.valid_until"
                                    :format="'dd-MM-yyyy'"
                                    :enable-time-picker="false"
                                    :display-marker="false"
                                    :clearable="false"
                                    :timezone="'UTC'"
                                    :min-date="new Date()"
                                    :auto-apply="true"
                                ></VueDatePicker>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-5">
                <div class="card">
                    <div class="card-header py-2">Calculation</div>
                    <div class="card-body">
                        <div class="row g-3">
                            <div class="col-12">
                                <label for="discount" class="form-label">Total Amount</label>
                                <input
                                    readonly
                                    type="text"
                                    class="form-control"
                                    v-model="quotation.amount"
                                />
                            </div>
                            <div class="col-6">
                                <label for="discount" class="form-label">Gross Discount(%)</label>
                                <input
                                    name="discount"
                                    type="text"
                                    class="form-control"
                                    placeholder="Enter discount"
                                    v-model="quotation.discount"
                                    @input="validateInput('order', 1)"
                                    @keypress="allowNumbersOnly"
                                    @keydown="handleSpecialKeys"
                                />
                            </div>
                            <div class="col-6">
                                <label for="discount" class="form-label">Gross Discount</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    readonly
                                    v-model="quotation.discount_amount"
                                />
                            </div>
                            <div class="col-12">
                                <label for="discount" class="form-label">Gross Amount</label>
                                <input
                                    readonly
                                    type="text"
                                    class="form-control"
                                    :value="quotation.amount - quotation.discount_amount"
                                />
                            </div>

                            <div class="col-6">
                                <label for="discount" class="form-label">VAT Amount(%)</label>
                                <vue-multiselect
                                    v-model="quotation.tax"
                                    :options="[0, tax]"
                                    :searchable="false"
                                    :show-labels="false"
                                ></vue-multiselect>
                            </div>
                            <div class="col-6">
                                <label for="discount" class="form-label">VAT Amount</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    readonly
                                    v-model="quotation.tax_amount"
                                />
                            </div>
                            <!-- </div> -->
                            <div class="col-12">
                                <label for="total_amount" class="form-label">Net Price</label>
                                <input
                                    name="total_amount"
                                    type="text"
                                    class="form-control"
                                    readonly
                                    :value="'£' + quotation.total_amount"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <button
                    type="submit"
                    class="btn btn-theme float-end"
                    v-text="isProcessing ? 'Processing...' : (addOrEdit === 'add' ? 'Create' : 'Update')"
                    :disabled="isProcessing || !isFormValid"
                    :class="{'disabled': isProcessing || !isFormValid}"
                    @click="handleSubmit($event, createQuotation)"
                ></button>
            </div>
        </div>
    </Form>
</template>
<script>
import { appMixin } from "../mixin/appMixin";
import Select2 from 'vue3-select2-component';
import {Field} from "vee-validate";

export default {
    mixins: [appMixin],
    props: {
        data: {
            type: Object,
            default: () => {}
        },
        quotationNumber: {
            type: String,
            default: ''
        }
    },
    components: {
        Field,
        Select2
    },
    data() {
        return {
            addOrEdit: this.data ? 'edit' : 'add',
            isProcessing: false,
            isFormValid: true, // default should be false
            previewOrderImages: [],
            isValidated: true,
            tax: 20,
            quotation: {
                customer: null,
                property: null,
                name: null,
                email: '',
                phone: '',
                address: '',
                city: '',
                state: '',
                country: '',
                postal_code: '',
                status: {name: 'pending', label: 'Pending'},
                tax: 20, // in percentage
                discount: 0,
                amount: 0,
                item_price: 0,
                labour_cost: 0,
                discount_amount: 0,
                tax_amount: 0,
                total_amount: 0,
                comment: '',
                files: [],
                price: 0,
                template: null,
                valid_until: this.moment(new Date()).add(7, 'days'), // +7 days from today
            },
            //customerOptions: [],
            templates: [],
            customers: [],
        }
    },
    created() {
        this.prefillQuotation();
    },
    mounted() {
        this.getQuotationTemplates();
        this.getCustomers();
    },
    computed: {
        // Generate dynamic schemas for each service item
        schema() {
            const schemas = {
                name: 'required',
                email: 'required|email',
                phone: 'required',
                address: 'required',
                postal_code: 'required',
                price: 'required',
                template: 'required',
                property: 'required',
            };
            return schemas;
        },
        // Prepare quotation template options
        templateOptions() {
            return this.templates.map(template => {
                return {
                    id: template.id,
                    name: template.name,
                };
            });
        },
        customerOptions() {
            return this.customers.map(customer => {
                // Build address array with only non-empty values
                const addressParts = [
                    customer.address,
                    customer.address_2,
                    customer.city,
                    customer.postal_code
                ].filter(part => part && part.trim() !== '');

                const fullAddress = addressParts.join(', ');

                return {
                    id: customer.id,
                    name: customer.name,
                    email: customer.email,
                    phone: customer.phone_number,
                    address: customer.address,
                    postal_code: customer.postal_code,
                    name_with_address: customer.name + (fullAddress ? ' - ' + fullAddress : ''),
                };
            });
        },
        propertyOptions() {
            // First check if customer is selected
            if (!this.quotation.customer) {
                return [];
            }
            // Then get addresses for the selected customer
            let addresses = this.customers.find(customer => customer.id === this.quotation.customer.id).addresses;
            return addresses.map(address => {
                return {
                    id: address.id,
                    address: address.address,
                    address_1: address.address_1,
                    address_2: address.address_2,
                    city: address.city,
                    county: address.county,
                    postal_code: address.postal_code,
                    country: address.country,
                    full_address: address.full_address,
                    user_id: address.user_id,
                };
            });
        },
        customerValue() {
            return this.quotation?.customer?.id || '';
        },
        propertyValue() {
            return this.quotation?.property?.id || '';
        },
        templateValue() {
            return this.quotation?.template?.id || '';
        }
    },
    methods: {
        // Get quotation templates
        getQuotationTemplates() {
            axios.post('/api/admin/quotation-templates')
            .then(response => {
                this.templates = response.data.templates || [];

                // Prefill the quotation data
                this.prefillQuotation();
            })
            .catch(error => {
                console.log(error);
            });
        },
        selectTemplate(template) {

        },
        getCustomers() {
            axios.post('/api/admin/customers')
            .then(response => {
                this.customers = response.data.customers || [];

                this.prefillQuotation();
            })
            .catch(error => {
                console.log(error);
            });
        },
        prefillQuotation() {
            // Check if data is not empty
            if (this.data) {
                this.quotation.name = this.data.name;
                this.quotation.email = this.data.email;
                this.quotation.phone = this.data.phone;
                this.quotation.address = this.data.address;
                this.quotation.city = this.data.city;
                this.quotation.state = this.data.state;
                this.quotation.country = this.data.country;
                this.quotation.postal_code = this.data.postal_code;
                this.quotation.discount_amount = this.data.discount_amount;
                this.quotation.tax_amount = this.data.tax_amount;
                this.quotation.total_amount = this.data.total_amount;
                this.quotation.price = this.data.price;
                this.quotation.valid_until = this.data.valid_until || this.moment(new Date()).add(7, 'days');

                this.quotation.status = {
                    name: this.data.status,
                    label: this.$capitalize(this.data.status)
                };

                // Prepare selected customer object
                if(this.customers.length > 0 && this.data.customer_id) {
                    let customer = this.customers.find(customer => customer.id === this.data.customer_id);

                    if(customer) {
                        // Prepare name with address
                        let nameWithAddress = [
                            customer.address,
                            customer.address_2,
                            customer.city,
                            customer.postal_code
                        ].filter(part => part && part.trim() !== '').join(', ');

                        this.quotation.customer = {
                            id: customer.id,
                            name: customer.name,
                            email: customer.email,
                            phone: customer.phone,
                            address: customer.address,
                            postal_code: customer.postal_code,
                            name_with_address: customer.name + (nameWithAddress ? ' - ' + nameWithAddress : ''),
                        };

                        // Prepare selected property object
                        let properties = customer?.addresses?.find(address => address.id === this.data.address_id);

                        if(properties) {
                            this.quotation.property = {
                                id: properties.id,
                                address: properties.address,
                                address_1: properties.address_1,
                                address_2: properties.address_2,
                                city: properties.city,
                                county: properties.county,
                                postal_code: properties.postal_code,
                                country: properties.country,
                                full_address: properties.full_address,
                                user_id: properties.user_id,
                            };
                        }

                        if (this.$refs.quoteForm) {
                            this.$refs.quoteForm.setFieldValue('name', customer.id);
                            this.$refs.quoteForm.setFieldValue('property',  this.quotation.property.id);
                        }
                    }
                }

                // Prepare selected template object
                let template = this.templates.find(template => template.id === this.data.template_id);

                if(template) {
                    this.quotation.template = {
                        id: template.id,
                        name: template.name,
                    };

                    if (this.$refs.quoteForm) {
                        this.$refs.quoteForm.setFieldValue('template', template.id);
                    }
                }
            }
        },
        onFileChange(event) {
            for (let file of event.target.files) {
                try {
                    let reader = new FileReader();

                    // Display the image preview
                    reader.onload = (e) => {
                        this.previewOrderImages.push(e.target.result);
                    }
                    reader.readAsDataURL(file); // Not sure if this will work in this context.
                    this.quotation.files.push(file);
                } catch {}
            }
        },
        calculateTotalAmount() {
            // Calculate total amount for all services
            let totalAmount = Number(this.quotation.price) || 0;
            let totalItemAmount = 0;
            let totalLabourCost = 0;
            console.log(totalAmount);

            // Get the overall discount
            const discountPercentage    = Number(this.quotation.discount) || 0;
            const discount              = (totalAmount * discountPercentage) / 100;
            const discountedAmount      = totalAmount - discount;

            // Apply the VAT on the discounted amount
            const vat       = (discountedAmount * Number(this.quotation.tax)) / 100;
            const finalAmount = discountedAmount + vat;

            // Update the total amount in the order object with proper decimal formatting
            this.quotation.amount           = parseFloat(totalAmount).toFixed(2);
            this.quotation.item_price       = parseFloat(totalItemAmount).toFixed(2);
            this.quotation.labour_cost      = parseFloat(totalLabourCost).toFixed(2);
            this.quotation.discount_amount  = parseFloat(discount).toFixed(2);
            this.quotation.tax_amount       = parseFloat(vat).toFixed(2);
            this.quotation.total_amount     = parseFloat(finalAmount).toFixed(2);
        },
        validateInput(type, index) {
            // Ensure the entered value is not greater than 100
            if(type === 'quotation') {
                if (this.quotation.discount > 100) {
                    this.quotation.discount = 100;
                }
            }
        },
        allowNumbersOnly(event) {
            // Allow only numeric input
            const charCode = event.charCode;
            // allow only number keys and numpad number keys
            if (
                (charCode >= 48 && charCode <= 57) ||  // Regular number keys
                (charCode >= 96 && charCode <= 105)  // Numpad number keys
            ) {
                return;
            }
        },
        handleSpecialKeys(event) {
            // Handle special keys like arrow keys, delete, and backspace
            const specialKeys = [8, 46, 37, 39];
            if(specialKeys.includes(event.keyCode) || ((event.keyCode >= 48 && event.keyCode <= 57) ||(event.keyCode >= 96 && event.keyCode <= 105))) {
                return;
            }
            event.preventDefault();
        },
        createQuotation() {
            let self = this;
            this.isProcessing = true;
            const config = {
                headers: {
                    'content-type': 'multipart/form-data',
                }
            }
            const formData = new FormData();
            Object.keys(this.quotation).forEach(key => {
                formData.append(key, this.quotation[key]);
            });

            // Check for crete or update
            let route = '/admin/quick-quotations/store';
            if (this.data) {
                route = '/admin/quick-quotations/update/' + this.data.id;
            }
            axios.post(route, this.quotation, config)
                .then(response => {
                    if (response.data.status === 'success') {
                        window.location.href = response.data.redirect_url;
                    }
                });

        },
        addCustomer(newCustomer) {
            const tag = {
                name: newCustomer,
                email: '',
                phone: '',
                name_with_address: newCustomer,
                address: '',
                postal_code: ''
            }
            this.customerOptions.push(tag)
            this.quotation.customer = tag;
            this.quotation.name = newCustomer;
        },
        selectCustomer(event) {
            this.quotation.name = event.name;
            this.quotation.email = event.email;
            this.quotation.phone = event.phone;
        },
        selectProperty(property) {
            // Build address array with only non-empty values
            const addressParts = [
                property.address,
                property.address_1,
                property.address_2,
                property.city,
                property.county
            ].filter(part => part && part.trim() !== '');

            const fullAddress = addressParts.join(', ');
            this.quotation.address = fullAddress;
            this.quotation.postal_code = property.postal_code;
        }
    },
    watch: {
        'quotation.price': function(newPrice, oldPrice) {
            this.calculateTotalAmount();
        },
        'quotation.discount': function(newDiscount, oldDiscount) {
            // Recalculate the total amount for all services
            this.calculateTotalAmount();
        },
        // When change the TAX amount
        'quotation.tax': function(newTax, oldTax) {
            // Recalculate the total amount for all services
            this.calculateTotalAmount();
        }
    }
}
</script>
<style scoped>
.multiselect {
    min-height: 36px !important;
}
</style>
